import React, { useState, useEffect } from 'react'
import {
	Modal,
	TextInput,
	Select,
	Button,
	Group,
	Text,
	Anchor,
	ActionIcon,
	Box,
	Divider,
} from '@mantine/core'
import Pencil from 'tabler-icons-react/dist/icons/pencil'
import Trash from 'tabler-icons-react/dist/icons/trash'
import Plus from 'tabler-icons-react/dist/icons/plus'
import { getWarningResolutionHref } from '../pages/warningUtils'

const WarningsModal = ({ opened, onClose, onSave, initial }) => {
	const [warnings, setWarnings] = useState([])
	const [editingIndex, setEditingIndex] = useState(null)
	const [newTitle, setNewTitle] = useState('')
	const [newLevel, setNewLevel] = useState('Warning')
	const [newResolutionLink, setNewResolutionLink] = useState('')
	const [addingNew, setAddingNew] = useState(false)

	useEffect(() => {
		setWarnings(initial ? [...initial] : [])
		setEditingIndex(null)
		setNewTitle('')
		setNewLevel('Warning')
		setNewResolutionLink('')
		setAddingNew(false)
	}, [initial, opened])

	const handleEdit = (index) => {
		const warning = warnings[index]
		setNewTitle(warning.title)
		setNewLevel(warning.level)
		setNewResolutionLink(warning.resolutionLink || '')
		setEditingIndex(index)
		setAddingNew(true)
	}

	const handleDelete = (index) => {
		if (window.confirm('Are you sure you want to remove this warning?')) {
			const updated = [...warnings]
			updated.splice(index, 1)
			setWarnings(updated)
		}
	}

	const handleAddOrUpdate = () => {
		const title = newTitle.trim()
		const resolutionLink = newResolutionLink.trim()
		if (!title || !newLevel || (resolutionLink && !getWarningResolutionHref(resolutionLink))) {
			return
		}
		const updated = [...warnings]
		const newWarning = {
			title,
			level: newLevel,
			...(resolutionLink ? { resolutionLink } : {}),
		}

		if (editingIndex !== null) {
			updated[editingIndex] = newWarning
		} else {
			updated.push(newWarning)
		}

		setWarnings(updated)
		setNewTitle('')
		setNewLevel('Warning')
		setNewResolutionLink('')
		setEditingIndex(null)
		setAddingNew(false)
	}

	const handleModalDone = () => {
		onSave(warnings)
		onClose()
	}

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title='Edit Warnings'
			centered
			size='lg'
		>
			{warnings.length === 0 && (
				<Text color='dimmed' fs='italic' mb='md'>
					No warnings added yet.
				</Text>
			)}

			{warnings.map((warning, index) => (
				<Group key={index} position='apart' mb='xs'>
					<Box>
						<Text>
							<strong>{warning.level}</strong>: {warning.title}
						</Text>
						{getWarningResolutionHref(warning.resolutionLink) && (
							<Anchor
								href={getWarningResolutionHref(warning.resolutionLink)}
								target='_blank'
								rel='noopener noreferrer'
								size='sm'
							>
								{warning.resolutionLink}
							</Anchor>
						)}
					</Box>
					<Group spacing={4}>
						<ActionIcon onClick={() => handleEdit(index)}>
							<Pencil size={16} />
						</ActionIcon>
						<ActionIcon color='red' onClick={() => handleDelete(index)}>
							<Trash size={16} />
						</ActionIcon>
					</Group>
				</Group>
			))}

			{addingNew ? (
				<Box mt='md'>
					<TextInput
						label='Title'
						value={newTitle}
						onChange={(e) => setNewTitle(e.target.value)}
						required
						mb='sm'
					/>
					<Select
						label='Level'
						value={newLevel}
						onChange={setNewLevel}
						data={['Warning', 'Critical']}
						required
						mb='sm'
					/>
					<TextInput
						label='Resolution link'
						description='Optional URL the traveler can use to resolve this warning before check-in.'
						placeholder='https://www.example.com'
						value={newResolutionLink}
						onChange={(e) => setNewResolutionLink(e.target.value)}
						error={
							newResolutionLink.trim() &&
							!getWarningResolutionHref(newResolutionLink)
								? 'Enter a valid HTTP(S) URL.'
								: null
						}
						mb='sm'
					/>
					<Group position='right'>
						<Button
							variant='subtle'
							color='gray'
							size='xs'
							onClick={() => {
								setNewTitle('')
								setNewLevel('Warning')
								setNewResolutionLink('')
								setEditingIndex(null)
								setAddingNew(false)
							}}
						>
							Cancel
						</Button>
						<Button size='xs' onClick={handleAddOrUpdate}>
							{editingIndex !== null ? 'Update' : 'Add'}
						</Button>
					</Group>
				</Box>
			) : (
				<Button
					variant='outline'
					leftIcon={<Plus size={16} />}
					size='xs'
					mt='md'
					onClick={() => setAddingNew(true)}
				>
					Add Warning
				</Button>
			)}

			<Divider my='md' />

			<Group position='right'>
				<Button onClick={handleModalDone}>Done</Button>
			</Group>
		</Modal>
	)
}

export default WarningsModal
